/*
 * @providesModule AccessibilityHelpers
 * 
 * Utility functions for generating consistent accessibility labels
 */

/**
 * Options for customizing the accessibility label with additional context.
 * Only one option is applied, in order of priority: context > index.
 */
interface AccessibilityLabelOptions {
  /** Position of the element in a list (0-based). Displayed as 1-based in the label.
   *  e.g. index: 2 → "Component_3" */
  index?: number;
  /** A descriptive qualifier for the element's state, variant, or dynamic value.
   *  e.g. context: 'Selected' → "Component_Selected"
   *       context: '$4.99' → "Component_$4.99" */
  context?: string;
}

/**
 * Creates a consistent, underscore-separated accessibility label
 * for identifying UI elements. Screen context is tracked via DataDog RUM startView at the route level.
 *
 * @param componentName - A short description of the UI element (e.g. 'PlayButton', 'Cell').
 * @param options - Optional extra segment appended to the label.
 *                  Priority order: context → index (only one is used).
 *
 * @returns A formatted label like "PlayButton" or "DepositTile_$4.99" or "MatchCell_1"
 *
 * @example
 * // Simple label
 * createAccessibilityLabel('PlayButton');
 * // → "PlayButton"
 *
 * @example
 * // With list index (0-based input, 1-based output)
 * createAccessibilityLabel('MatchCell', { index: 0 });
 * // → "MatchCell_1"
 *
 * @example
 * // With context (state, variant, or dynamic value)
 * createAccessibilityLabel('Tab', { context: 'Selected' });
 * // → "Tab_Selected"
 *
 * @example
 * // With context for dynamic values
 * createAccessibilityLabel('DepositTile', { context: '$4.99', index: 1 });
 * // → "DepositTile_$4.99_1"
 */
export const createAccessibilityLabel = (
  componentName: string,
  options?: AccessibilityLabelOptions,
): string => {
  const parts = [componentName];

  if (options?.context) {
    parts.push(options.context);
  } if (options?.index !== undefined) {
    parts.push(String(options.index + 1));
  }

  return parts.join('_');
};
